/**
 * Soldier - Individual soldier in the player's army.
 *
 * Behavior changes for dense swarm mode:
 * - No formation jitter/wiggle.
 * - Always uses run animation for a constant forward-motion read.
 * - Uniform scale for all soldiers.
 */

import Phaser from 'phaser';

export interface SoldierConfig {
  x: number;
  y: number;
}

export class Soldier extends Phaser.GameObjects.Container {
  private sprite: Phaser.GameObjects.Sprite;
  private targetX: number = 0;
  private targetY: number = 0;

  static readonly FRAME_SIZE = 48;
  static readonly SCALE = 0.38;
  static readonly RADIUS = (Soldier.FRAME_SIZE * Soldier.SCALE) / 2;
  private static readonly MOVE_LERP = 0.22;

  constructor(scene: Phaser.Scene, config: SoldierConfig) {
    super(scene, config.x, config.y);

    this.targetX = config.x;
    this.targetY = config.y;

    this.sprite = scene.add.sprite(0, 0, 'soldier', 0);
    this.sprite.setScale(Soldier.SCALE);
    if (this.sprite.anims) {
      this.sprite.play('soldier_walk');
    }

    if (this.sprite.preFX) {
      this.sprite.preFX.addGlow(0x000000, 1, 0, false, 0.1, 16);
    }

    this.add(this.sprite);
    scene.add.existing(this);
  }

  setTargetPosition(x: number, y: number): void {
    this.targetX = x;
    this.targetY = y;
  }

  update(): void {
    this.x = Phaser.Math.Linear(this.x, this.targetX, Soldier.MOVE_LERP);
    this.y = Phaser.Math.Linear(this.y, this.targetY, Soldier.MOVE_LERP);

    if (this.sprite.anims?.currentAnim?.key !== 'soldier_walk') {
      this.sprite.play('soldier_walk', true);
    }
  }

  getRadius(): number {
    return Soldier.RADIUS;
  }

  /**
   * Keep soldiers uniform: spawn feedback uses alpha only (no scale pulses).
   */
  playSpawnAnimation(): void {
    this.setAlpha(0.35);
    this.scene.tweens.add({
      targets: this,
      alpha: 1,
      duration: 120,
      ease: 'Quad.easeOut',
    });
  }

  /**
   * Shooting feedback keeps run loop intact; no shoot animation override.
   */
  playShootAnimation(): void {
    this.sprite.setTintFill(0xFFFFFF);
    this.scene.time.delayedCall(20, () => {
      if (this.active) {
        this.sprite.clearTint();
      }
    });
  }

  /**
   * Hit feedback uses tint only to preserve uniform size in the swarm.
   */
  playHitEffect(): void {
    this.sprite.setTint(0xFF4444);
    this.scene.time.delayedCall(80, () => {
      if (this.active) {
        this.sprite.clearTint();
      }
    });
  }

  playDeathAnimation(onComplete?: () => void): void {
    this.scene.tweens.add({
      targets: this,
      scaleX: 0,
      scaleY: 0,
      alpha: 0,
      duration: 150,
      ease: 'Power2',
      onComplete: () => {
        if (onComplete) onComplete();
        this.destroy();
      },
    });
  }
}
